import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CONCILIACAO_IUGU,
  SALDO_CONTA_MESTRE_IUGU,
  RECONCILIAR_PAGAMENTO,
  ConciliacaoIuguData,
  ConciliacaoIuguVars,
  SaldoContaMestreIuguData,
  ReconciliarPagamentoData,
  ReconciliarPagamentoVars,
  ResultadoConciliacao,
  ResultadoReconciliacao,
  DivergenciaConciliacao,
  PagamentoReconciliado,
} from '../services/conciliacaoService';
import type { PeriodoFinanceiro } from '../types/financeiro';

// financeiro-conciliacao-iugu Phase 5 (Task 5) -- molde direto de useFinanceiroPlataforma.ts
// (useState de período, fetchPolicy: 'no-cache' em toda useQuery), com UMA diferença deliberada:
// conciliacaoIugu/saldoContaMestreIugu falham por motivos diferentes (a primeira DEGRADA via
// gatewayDisponivel: false; a segunda LANÇA erro GraphQL, ver Task 1/3 do plano) -- por isso
// `conciliacaoError`/`saldoError` (e os `loading` irmãos) são expostos ISOLADOS por query, nunca
// um gate único combinado (docs/anti-patterns.md:207-230, "reusar a mensagem de período sem
// movimento para uma falha de rede isolada").

export interface UseConciliacaoIuguResult {
  periodo: PeriodoFinanceiro;
  setPeriodo: (periodo: PeriodoFinanceiro) => void;

  resultado: ResultadoConciliacao | null;
  conciliacaoLoading: boolean;
  conciliacaoError: Error | undefined;
  refetchConciliacao: () => void;

  saldo: number | null;
  saldoConsultadoEm: string | null;
  saldoLoading: boolean;
  saldoError: Error | undefined;
  refetchSaldo: () => void;

  reconciliar: (pagamentoId: string) => Promise<void>;
  reconciliando: boolean;
  // Desfecho da última reconciliação -- UMA mensagem só, seja ela sucesso, desfecho esperado
  // (fatura inexistente) ou falha de infraestrutura. `reconciliar` NUNCA rejeita: o call site é um
  // onClick, e uma promise rejeitada ali vira unhandled rejection (overlay em dev, silêncio total
  // em produção -- o admin clicava e nada acontecia).
  reconciliarAviso: AvisoReconciliacao | null;
  limparReconciliarAviso: () => void;
}

export interface AvisoReconciliacao {
  severidade: 'success' | 'info' | 'warning' | 'error';
  mensagem: string;
}

// A severidade é derivada do desfecho que o BACKEND informou, nunca de heurística sobre o texto.
// `error` fica reservado ao que impediu concluir a operação (o `onError` abaixo) -- fatura
// inexistente é um desfecho conhecido, não uma falha, e por isso é `warning`.
const SEVERIDADE_POR_RESULTADO: Record<ResultadoReconciliacao, AvisoReconciliacao['severidade']> = {
  ATUALIZADO: 'success',
  JA_CONCILIADO: 'info',
  INVOICE_INEXISTENTE: 'warning',
  SEM_INVOICE_LOCAL: 'warning',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateLocal(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Últimos 7 dias (hoje inclusive), aritmética de Date LOCAL (nunca UTC) -- dentro do teto de 92
// dias que ConciliacaoIuguService.conciliar recusa com BusinessException (AC-10).
function periodoUltimos7Dias(): PeriodoFinanceiro {
  const hoje = new Date();
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 6);
  return { inicio: formatDateLocal(inicio), fim: formatDateLocal(hoje) };
}

export function useConciliacaoIugu(): UseConciliacaoIuguResult {
  const [periodo, setPeriodo] = useState<PeriodoFinanceiro>(periodoUltimos7Dias());
  const [saldoConsultadoEm, setSaldoConsultadoEm] = useState<string | null>(null);

  const conciliacaoQuery = useQuery<ConciliacaoIuguData, ConciliacaoIuguVars>(CONCILIACAO_IUGU, {
    variables: { inicio: periodo.inicio, fim: periodo.fim },
    fetchPolicy: 'no-cache',
    // Sem isto, `loading` NÃO volta a true durante um refetch (comportamento padrão do Apollo) --
    // e a ação "Atualizar" ficava sem nenhum sinal de progresso, mesmo com o componente já
    // preparado para exibi-lo. Mesmo idioma de useFasesGrupoPartidas.ts.
    notifyOnNetworkStatusChange: true,
  });

  const saldoQuery = useQuery<SaldoContaMestreIuguData>(SALDO_CONTA_MESTRE_IUGU, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  // saldoContaMestreIugu não devolve nenhum equivalente a `consultadoEm` (é um Decimal! puro,
  // ver conciliacao.graphqls) -- a "idade do dado" deste bloco é rastreada no cliente, no momento
  // em que a consulta mais recente chega com sucesso (AC-14 ainda exige a indicação de idade
  // neste bloco, mesmo sem o servidor fornecê-la).
  useEffect(() => {
    if (saldoQuery.data) {
      setSaldoConsultadoEm(new Date().toISOString());
    }
  }, [saldoQuery.data]);

  const [reconciliarAviso, setReconciliarAviso] = useState<AvisoReconciliacao | null>(null);
  // Estado local sobreposto ao resultado da query: guarda o pagamento devolvido pela mutation,
  // por id. É o que permite atualizar APENAS a linha que tentou reconciliar -- refazer
  // `conciliacaoIugu` dispararia uma varredura ao vivo contra a Iugu (centenas de faturas) para
  // atualizar uma linha só.
  const [linhasAtualizadas, setLinhasAtualizadas] = useState<Record<string, PagamentoReconciliado>>(
    {},
  );

  const [executarReconciliar, { loading: reconciliando }] = useMutation<
    ReconciliarPagamentoData,
    ReconciliarPagamentoVars
  >(RECONCILIAR_PAGAMENTO, {
    // onError é o que impede a unhandled rejection: com ele, a promise de `executarReconciliar`
    // RESOLVE em vez de rejeitar (contrato do Apollo). Só um try/catch no call site não bastava --
    // o Apollo ainda emitia a rejeição pela cadeia interna do observable, e era ela que o overlay
    // do CRA capturava, POR CIMA da mensagem que já estava na tela.
    onError: (erro) => {
      setReconciliarAviso({ severidade: 'error', mensagem: erro.message });
    },
  });

  const reconciliar = async (pagamentoId: string): Promise<void> => {
    setReconciliarAviso(null);
    const retorno = await executarReconciliar({ variables: { pagamentoId } });
    const resultado = retorno.data?.reconciliarPagamento;
    if (!resultado) {
      // Caminho de erro: o aviso já foi montado pelo onError acima. Nada a atualizar na linha,
      // porque não sabemos o desfecho.
      return;
    }
    setReconciliarAviso({
      severidade: SEVERIDADE_POR_RESULTADO[resultado.resultado],
      mensagem: resultado.mensagem,
    });
    setLinhasAtualizadas((atual) => ({ ...atual, [resultado.pagamento.id]: resultado.pagamento }));
  };

  // A varredura é a fonte da verdade; as linhas reconciliadas nesta sessão são sobrepostas por
  // cima dela. Um "Atualizar" refaz a query e traz o estado real do servidor -- as sobreposições
  // continuam idênticas ao que veio, então não há divergência a resolver.
  const resultadoConciliacao = conciliacaoQuery.data?.conciliacaoIugu ?? null;
  const resultadoComLinhasAtualizadas: ResultadoConciliacao | null =
    resultadoConciliacao && Object.keys(linhasAtualizadas).length > 0
      ? {
          ...resultadoConciliacao,
          divergencias:
            resultadoConciliacao.divergencias?.map((d: DivergenciaConciliacao) => {
              const atualizado = d.pagamentoId ? linhasAtualizadas[d.pagamentoId] : undefined;
              return atualizado
                ? {
                    ...d,
                    statusLocal: atualizado.status,
                    validadoIuguEm: atualizado.validadoIuguEm,
                    invoiceInexistenteEm: atualizado.invoiceInexistenteEm,
                  }
                : d;
            }) ?? null,
        }
      : resultadoConciliacao;

  return {
    periodo,
    setPeriodo,

    resultado: resultadoComLinhasAtualizadas,
    conciliacaoLoading: conciliacaoQuery.loading,
    conciliacaoError: conciliacaoQuery.error,
    refetchConciliacao: () => {
      conciliacaoQuery.refetch();
    },

    saldo: saldoQuery.data?.saldoContaMestreIugu ?? null,
    saldoConsultadoEm,
    saldoLoading: saldoQuery.loading,
    saldoError: saldoQuery.error,
    refetchSaldo: () => {
      saldoQuery.refetch();
    },

    reconciliar,
    reconciliando,
    reconciliarAviso,
    limparReconciliarAviso: () => setReconciliarAviso(null),
  };
}
